import type { QueueViewId, StockAdminQueue } from '@myrewards/contracts';
import type { ReactElement } from 'react';
import { OpsSidebar, type OpsSidebarGroup } from '../../shell';
import { Card } from '../../ui';
import { QUEUE_VIEW } from '../labels';
import s from './QueueViews.module.css';

/** How the views group in the sidebar, as HOME2 and NAV2 draw them. */
const GROUP: Record<QueueViewId, string> = {
  queue: 'Requests',
  mine: 'Requests',
  tax: 'Requests',
  in_progress: 'Requests',
  past_deadline: 'Employees',
  letters: 'Employees',
  earning_statements: 'After payroll',
  shareworks: 'After payroll',
};

/** The sidebar's groups, in the queue read's order, each view with its count when it has one. */
export function queueViewGroups(
  queue: Pick<StockAdminQueue, 'views'>,
  queueHref: string,
): OpsSidebarGroup[] {
  const groups: OpsSidebarGroup[] = [];
  for (const view of queue.views) {
    const label = GROUP[view.id];
    let group = groups.find((g) => g.label === label);
    if (!group) {
      group = { label, items: [] };
      groups.push(group);
    }
    group.items = [
      ...group.items,
      {
        id: view.id,
        label: QUEUE_VIEW[view.id].label,
        href: view.id === 'queue' ? queueHref : QUEUE_VIEW[view.id].href,
        // A badge with nothing to count is hidden, never a bare "0" (APP-093, HOME2.empty).
        ...(view.count > 0 && { count: view.count }),
        end: view.id === 'queue',
        tone: view.id === 'past_deadline' && view.count > 0 ? 'problem' : 'default',
      },
    ];
  }
  return groups;
}

/**
 * The queue's Level 2 (NAV2, APP-087 §4): one left sidebar of views with their counts, the same
 * on the Stock Admin's Home and on `/queue` (DECISION-LOG R3). On Home the queue is the page
 * itself, so its Queue item is Home. One card, titled Views with its help as HOME2 draws it; the
 * sidebar inside draws no box of its own (APP-067 §13).
 */
export function QueueViews({
  queue,
  queueHref,
}: {
  queue: Pick<StockAdminQueue, 'views'>;
  /** Where Queue goes: `/` on Home, where the queue is the page, else Manage › Queue. */
  queueHref: '/' | '/manage/queue';
}): ReactElement {
  return (
    <div className={s.views}>
      <Card
        title="Views"
        compact
        titleSize="sm"
        help={{
          shows:
            'The queue’s views with how many each holds: the requests to claim and work, the employees past their deadline or at letters, and what comes after Payroll.',
          canDo:
            'Open My queue to see your claims, or choose a stage to narrow the list. The main queue includes all active submitted awards.',
        }}
      >
        <OpsSidebar
          label="Queue views"
          groups={queueViewGroups(queue, queueHref)}
          className={s.plain}
          headingLevel={3}
        />
      </Card>
    </div>
  );
}
